import Acquisition01 from '../components/micro/caseStudy/childNodes/Acquisition01';
import Acquisition02 from '../components/micro/caseStudy/childNodes/Acquisition02';
import Acquisition03 from '../components/micro/caseStudy/childNodes/Acquisition03';
import Acquisition04 from '../components/micro/caseStudy/childNodes/Acquisition04';
import AdditionalCaseStudyImaging01 from '../components/micro/AdditionalCaseStudies/childNodes/AdditionalCaseStudyImaging01';
import AdditionalCaseStudyImaging02 from '../components/micro/AdditionalCaseStudies/childNodes/AdditionalCaseStudyImaging02';
import AdditionalCaseStudyImaging03 from '../components/micro/AdditionalCaseStudies/childNodes/AdditionalCaseStudyImaging03';
import AdditionalCaseStudyImaging04 from '../components/micro/AdditionalCaseStudies/childNodes/AdditionalCaseStudyImaging04';
import AdditionalCaseStudyImaging05 from '../components/micro/AdditionalCaseStudies/childNodes/AdditionalCaseStudyImaging05';
import AdditionalCaseStudyImaging06 from '../components/micro/AdditionalCaseStudies/childNodes/AdditionalCaseStudyImaging06';
import { ImageFormat } from './learningCheck.constants';
import { IMAGE_PATHS, VIDEO_PATHS } from './paths.constants';

export interface PresentationSectionFormat {
  sectionName: string;
  itemsList?: string[];
  itemMap?: { key: string; value: string }[];
}

export interface ImagingSectionFormat {
  heading: string;
  paragraph?: string;
  warning?: string;
  videoPath?: string;
  footnote?: string;
  childNode?: () => JSX.Element;
  dicomIndex?: number;
  imagePath?: ImageFormat;
}

export interface FindingsSectionFormat {
  sectionName?: string;
  itemsList?: (string | string[] | ImageFormat)[];
  findingsImage?: ImageFormat;
  childNode?: () => JSX.Element;
}

export interface CaseStudyFormat {
  presentation: PresentationSectionFormat[];
  imaging: ImagingSectionFormat;
  findings: FindingsSectionFormat[];
  headshot: ImageFormat;
  title?: string;
  noteParagraphs?: string[];
}

export interface CaseStudyDictionary {
  [module: string]: {
    [page: string]: CaseStudyFormat | CaseStudyFormat[];
  };
}

const CASE_STUDY: CaseStudyDictionary = {
  axuminInterpretation: {
    '10': {
      headshot: IMAGE_PATHS.CASE_STUDY[1].CASE_STUDY_HEADSHOT_1,
      presentation: [
        {
          sectionName: 'PATIENT INFORMATION',
          itemsList: ['Post-radiotherapy (remote) for prostate cancer', 'Now rising PSA'],
        },
      ],
      imaging: {
        heading: 'IMAGING INTERPRETATION VIDEO',
        paragraph:
          'Please watch this video to better understand the steps to optimize image interpretation when reviewing DICOM images',
        videoPath: VIDEO_PATHS.INT_CASE_STUDY_1.path,
        footnote:
          'Recording was provided courtesy of David M. Schuster MD, FACR. Director. Division of Nuclear Medicine and Molecular Imaging. Emory University.',
      },
      findings: [
        {
          sectionName: 'IMAGING FINDINGS',
          itemsList: [
            'Extensive retroperitoneal malignant nodes extending into thorax',
            'Probable benign mild diffuse midline prostate activity',
            'Incidental findings:',
            [
              '– Left adrenal hyperplasia/adenoma',
              '– Right arm vein focal activity (due to retention along vessel valves)',
              '– Benign-appearing right iliac sclerosis',
            ],
          ],
        },
      ],
    },
    '11': {
      headshot: IMAGE_PATHS.CASE_STUDY[1].CASE_STUDY_HEADSHOT_2,
      presentation: [
        {
          sectionName: 'PATIENT PRESENTATION',
          itemsList: [
            'Post radical prostatectomy, negative nodal dissection',
            'Now rising PSA to 0.73 ng/mL',
            'Negative MR for malignancy',
            'Earlier negative skeletal screening',
          ],
        },
      ],
      imaging: {
        heading: 'IMAGING INTERPRETATION VIDEO',
        paragraph:
          'Please watch this video to better understand the steps to optimize image interpretation when reviewing DICOM images',
        videoPath: VIDEO_PATHS.INT_CASE_STUDY_2.path,
        footnote:
          'Recording was provided courtesy of David M. Schuster MD, FACR. Director. Division of Nuclear Medicine and Molecular Imaging. Emory University.',
      },
      findings: [
        {
          sectionName: 'IMAGING FINDINGS',
          itemsList: [
            'Intense small left presacral node, 3 x 4 mm, measure SUV',
            'Axumin negative sclerotic lesions (T8, L1, L4, L5, left acetabulum, right ilium) needing evaluation. Review prior skeletal screening. Is further imaging needed?',
            'Schwannoma right sacrum (confirmed on MR)',
            'Goiter',
            'Post-prostatectomy bladder',
            'Benign left lymphocele',
            'Mild symmetric inguinal nodal activity',
            'Sinus disease',
          ],
        },
      ],
    },
    '13': {
      headshot: IMAGE_PATHS.CASE_STUDY[1].CASE_STUDY_HEADSHOT_3,
      presentation: [
        {
          sectionName: 'PATIENT PRESENTATION',
          itemsList: [
            'Post robotic-assisted laparoscopic radical prostatectomy and bilateral staging pelvic lymphadenectomy',
            'Now rising PSA to 0.41 ng/mL',
          ],
        },
      ],
      imaging: {
        heading: 'IMAGING INTERPRETATION VIDEO',
        paragraph:
          'Please watch this video to better understand the steps to optimize image interpretation when reviewing DICOM images',
        videoPath: VIDEO_PATHS.INT_CASE_STUDY_3.path,
        footnote:
          'Recording was provided courtesy of David M. Schuster MD, FACR. Director. Division of Nuclear Medicine and Molecular Imaging. Emory University.',
      },
      findings: [
        {
          sectionName: 'IMAGING FINDINGS',
          itemsList: [
            'Single intense right internal iliac/pelvic side wall node (1 cm), consistent with metastasis',
            'No uptake in prostate bed',
            'Symmetric mild uptake in negative inguinal nodes',
            'Bilateral benign lymphoceles',
            'Otherwise negative',
          ],
        },
      ],
    },
    '16': {
      headshot: IMAGE_PATHS.CASE_STUDY[1].CASE_STUDY_HEADSHOT_4,
      presentation: [
        {
          sectionName: 'PATIENT PRESENTATION',
          itemsList: [
            'Radical prostatectomy Gleason 7, T3b (right)',
            'Salvage radiation therapy',
            'Now PSA rise to 3.2 ng/mL',
          ],
        },
      ],
      imaging: {
        heading: 'IMAGING INTERPRETATION VIDEO',
        paragraph:
          'Please watch this video to better understand the steps to optimize image interpretation when reviewing DICOM images',
        videoPath: VIDEO_PATHS.INT_CASE_STUDY_4.path,
        footnote:
          'Recording was provided courtesy of David M. Schuster MD, FACR. Director. Division of Nuclear Medicine and Molecular Imaging. Emory University.',
      },
      findings: [
        {
          sectionName: 'IMAGING FINDINGS',
          itemsList: [
            'No suspicious activity in bed',
            'Left sacral bone metastasis',
            '5- to 6-mm left common iliac near bifurcation node; activity similar to marrow but intense for size',
            'Also, tiny 2- to 3-mm left obturator/external iliac mild uptake; may also be suspect in light of other disease but would not call positive in isolation',
          ],
        },
        {
          sectionName: 'FOLLOW-UP',
          itemsList: ['MR confirmed sacral metastasis; suspicious left iliac nodes (though not a proof)'],
        },
      ],
    },
  },
  axuminAcquisition: {
    '11': [
      {
        title: 'Case Study 1',
        headshot: IMAGE_PATHS.CASE_STUDY[1].CASE_STUDY_HEADSHOT_1,
        presentation: [
          {
            sectionName: 'CLINICAL HISTORY',
            itemMap: [
              { key: 'Age', value: '65 years of age' },
              { key: 'Primary Stage', value: 'T3a N0 M0' },
              { key: 'Prior Therapy', value: 'Radiotherapy (EBRT/IMRT)' },
              { key: 'PSA', value: '8 ng/mL' },
              { key: 'Gleason', value: '7' },
              { key: 'Prior Imaging', value: 'Planar bone scan, CE-CT, pelvic, & prostate MRI' },
              { key: 'Reason for Scan', value: 'Suspected recurrent prostate cancer' },
            ],
          },
          {
            sectionName: 'ACQUISITION',
            itemMap: [
              { key: 'PET/CT', value: 'Siemens Biograph&trade; TruePoint&trade;' },
              { key: 'Weight', value: '75 kg' },
              { key: 'Administered Activity', value: '371 MBq' },
              { key: 'Time after Administration', value: '4 m 53 s' },
              { key: 'Reconstruction', value: 'PSF, ToF, 2i21s' },
            ],
          },
        ],
        imaging: {
          heading: 'IMAGING INTERPRETATION',
          paragraph: 'Please view the MIP below on the left-hand side to better interpret the case',
          childNode: Acquisition01,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: ['Extensive retroperitoneal malignant nodes extending into thorax'],
          },
          {
            sectionName: 'MANAGEMENT PLAN',
            itemsList: ['Intended: curative', 'Revised: palliative'],
          },
        ],
        noteParagraphs: [
          '<b>Note: </b>This case study is provided as an example of the interpretation and utility of Axumin<sup>®</sup> (fluciclovine F 18) PET/CT imaging results.',
          'Axumin is not indicated for directing or changing patient management.',
          'Such decisions must be based on the independent medical judgment of the physician(s) responsible for the individual patient’s management.',
        ],
      },
      {
        title: 'Case Study 2',
        headshot: IMAGE_PATHS.CASE_STUDY[1].CASE_STUDY_HEADSHOT_2,
        presentation: [
          {
            sectionName: 'CLINICAL HISTORY',
            itemMap: [
              { key: 'Age', value: '87 years of age' },
              { key: 'Primary Stage', value: 'T1A N0 M0' },
              { key: 'Prior Therapy', value: 'Radiotherapy (EBRT/IMRT) and ADT' },
              { key: 'PSA', value: '16.63 ng/mL' },
              { key: 'Gleason', value: '3 + 3' },
              { key: 'Prior Imaging', value: 'Planar bone scan; pelvic, & prostate MRI' },
            ],
          },
          {
            sectionName: 'ACQUISITION',
            itemMap: [
              { key: 'PET/CT', value: 'Siemens Biograph&trade; 40 mCT' },
              { key: 'Weight', value: '70.5 kg' },
              { key: 'Administered Activity', value: '9.6 mCi' },
              { key: 'Reconstruction', value: 'PSF, ToF, 2i21s' },
            ],
          },
        ],
        imaging: {
          heading: 'IMAGING FINDINGS',
          paragraph: 'Please view the MIP below on the left-hand side to better interpret the case',
          childNode: Acquisition02,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: ['Prostate (right apex and mid peripheral zone)', 'Bone (Vertebra T8)'],
          },
          {
            sectionName: 'HISTOPATHOLOGY',
            itemsList: ['Biopsy positive (prostate)'],
          },
          {
            sectionName: 'MANAGEMENT PLAN',
            itemsList: ['Intended: curative – Androgen Deprivation Therapy', 'Revised: no revision'],
          },
        ],
        noteParagraphs: [
          '<b>Note: </b>This case study is provided as an example of the interpretation and utility of Axumin<sup>®</sup> (fluciclovine F 18) PET/CT imaging results.',
          'Axumin is not indicated for directing or changing patient management.',
          'Such decisions must be based on the independent medical judgment of the physician(s) responsible for the individual patient’s management.',
        ],
      },
      {
        title: 'Case Study 3',
        headshot: IMAGE_PATHS.CASE_STUDY[1].CASE_STUDY_HEADSHOT_3,
        presentation: [
          {
            sectionName: 'CLINICAL HISTORY',
            itemMap: [
              { key: 'Age', value: '69 years of age' },
              { key: 'Primary Stage', value: 'T3B (pathological) N0 M0' },
              { key: 'Prior Therapy', value: 'Radical prostatectomy' },
              { key: 'PSA', value: '2.2 ng/mL' },
              { key: 'Gleason', value: '7' },
              { key: 'Prior Imaging', value: 'Pelvic and prostate CT, planar bone scan' },
              { key: 'Reason for Scan', value: 'Suspected recurrent prostate cancer' },
            ],
          },
          {
            sectionName: 'ACQUISITION',
            itemMap: [
              { key: 'PET/CT', value: 'Discovery STE | GE Healthcare' },
              { key: 'Weight', value: '44.8 kg' },
              { key: 'Administered Activity', value: '9.2 mCi' },
              { key: 'Reconstruction', value: '3D Iterative Reconstruction' },
            ],
          },
        ],
        imaging: {
          heading: 'IMAGING FINDINGS',
          paragraph: 'Please view the MIP below on the left-hand side to better interpret the case',
          childNode: Acquisition03,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: ['Bone left ilium', 'Lymph node right iliac'],
          },
          {
            sectionName: 'MANAGEMENT PLAN',
            itemsList: [
              'Intended: salvage radiotherapy to the prostate bed with boost to areas guided by conventional imaging',
              'Revised: salvage radiotherapy to the prostate bed with boost to areas guided by conventional imaging with ADT',
            ],
          },
        ],
        noteParagraphs: [
          '<b>Note: </b>This case study is provided as an example of the interpretation and utility of Axumin<sup>®</sup> (fluciclovine F 18) PET/CT imaging results.',
          'Axumin is not indicated for directing or changing patient management.',
          'Such decisions must be based on the independent medical judgment of the physician(s) responsible for the individual patient’s management.',
        ],
      },
      {
        title: 'Case Study 4',
        headshot: IMAGE_PATHS.CASE_STUDY[1].CASE_STUDY_HEADSHOT_4,
        presentation: [
          {
            sectionName: 'CLINICAL HISTORY',
            itemMap: [
              { key: 'Age', value: '65 years of age' },
              { key: 'Primary Stage', value: 'T2c (pathological) N0 M0' },
              { key: 'Prior Therapy', value: 'Radical prostatectomy' },
              { key: 'PSA', value: '4.60 ng/mL' },
              { key: 'Gleason', value: '7' },
              { key: 'Prior Imaging', value: 'Pelvic and prostate CT, planar bone scan' },
            ],
          },
          {
            sectionName: 'ACQUISITION',
            itemMap: [
              { key: 'PET/CT', value: 'Siemens Biograph 40 Truepoint' },
              { key: 'Weight', value: '93 kg' },
              { key: 'Administered Activity', value: '11.7 mCi' },
              { key: 'Reconstruction', value: 'PSF, ToF, 2i21s' },
            ],
          },
        ],
        imaging: {
          heading: 'IMAGING FINDINGS',
          paragraph: 'Please view the MIP below on the left-hand side to better interpret the case',
          childNode: Acquisition04,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: ['Left internal iliac lymph node uptake', 'Right ilium bone uptake'],
          },
          {
            sectionName: 'MANAGEMENT PLAN',
            itemsList: [
              'Intended: salvage radiotherapy to the prostate bed with boost to areas guided by conventional imaging',
              'Revised: salvage radiotherapy to the prostate bed with boost to areas guided by conventional imaging with ADT',
              '180-day follow-up PSA 0.10 ng/mL',
            ],
          },
        ],
        noteParagraphs: [
          '<b>Note: </b>This case study is provided as an example of the interpretation and utility of Axumin<sup>®</sup> (fluciclovine F 18) PET/CT imaging results.',
          'Axumin is not indicated for directing or changing patient management.',
          'Such decisions must be based on the independent medical judgment of the physician(s) responsible for the individual patient’s management.',
        ],
      },
    ],
  },
  additional: {
    '0': [
      {
        title: 'Case Study 1',
        headshot: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_HEADSHOT_1,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: ['Post-radiotherapy and ADT', 'Now rising PSA', 'Negative MR'],
          },
        ],
        imaging: {
          // Uncomment these 4 lines, and comment the following 3 lines if we ever need to bring back the DICOM viewer
          // heading: 'DICOM VIEWER',
          // paragraph: 'Please reviigew and evaluate the DICOM image',
          // warning: ' (Note: this may take a few minutes to load.)',
          // dicomIndex: 1,
          heading: 'PET/CT IMAGES',
          paragraph: 'Please review the case images below, then click the right arrow to review the findings.',
          imagePath: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_IMAGING_1,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: [
              'Right apex hot focus in mild diffuse background',
              'Negative nodes, bone',
              'Physiologic muscle, bladder tracer jets (percolation)',
            ],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: ['Biopsy positive right apex', 'Salvage brachytherapy; PSA nadir < 0.2 ng/mL'],
          },
          {
            sectionName: 'REVIEW IMAGING FINDINGS',
            itemsList: [
              {
                path: '/Axumintraining/assets/images/acquisition/CaseStudy1.png',
                alt: 'Imaging Findings',
              },
            ],
          },
        ],
      },
      {
        title: 'Case Study 2',
        headshot: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_HEADSHOT_2,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              'Radiation therapy for Gleason 7 prostate cancer; short-course ADT',
              'Now rising PSA to 29.7 ng/mL',
              'Delayed acquisition (22 min) whole body',
            ],
          },
        ],
        imaging: {
          // heading: 'DICOM VIEWER',
          // paragraph: 'Please review and evaluate the DICOM image',
          // warning: ' (Note: this may take a few minutes to load.)',
          // dicomIndex: 2,
          heading: 'PET/CT IMAGES',
          paragraph: 'Please review the case images below, then click the right arrow to review the findings.',
          imagePath: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_IMAGING_2,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: [
              'Left lobe positive; mild elsewhere, nonspecific',
              'Bones suspicious at T9, L2, L3, right ilium with negative CT; possible R femur; further investigation needed given focal activity on delayed imaging',
              'Nodes negative',
            ],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: ['Biopsy positive left lobe; suspicious findings on MR at L2 and L3'],
          },
          {
            sectionName: 'REVIEW IMAGING FINDINGS',
            itemsList: [
              {
                path: '/Axumintraining/assets/images/acquisition/CaseStudy2.png',
                alt: 'Imaging Findings',
              },
            ],
          },
        ],
      },
      {
        title: 'Case Study 3',
        headshot: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_HEADSHOT_3,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              'Radiation therapy for Gleason 9, salvage prostatectomy 2012, neuroendocrine differentiation',
              'Negative nodal dissection',
              'Now PSA 1.1 ng/mL',
            ],
          },
        ],
        imaging: {
          // heading: 'DICOM VIEWER',
          // paragraph: 'Please review and evaluate the DICOM image',
          // warning: ' (Note: this may take a few minutes to load.)',
          // dicomIndex: 3,
          heading: 'PET/CT IMAGES',
          paragraph: 'Please review the case images below, then click the right arrow to review the findings.',
          imagePath: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_IMAGING_3,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: [
              'Axillary vein activity on left; benign liver cyst/hemangioma',
              'Indeterminate bed; bulbous urethra equal to marrow at anastomosis, though not focal',
              'Abnormal nodes:',
              [
                '– Right paracaval at bifurcation; left obturator/external iliac',
                '– Subtle right external iliac node; not called as positive alone, but suspect in this case',
              ],
            ],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: [
              'No definitive level of proof; radiation therapy to right paracaval and left obturator nodes, mild PSA response but then increased with unchanged appearance on CT of nodes',
              'No bone disease on MR; on hormonal therapy, explanation may be right external iliac node, other microscopic disease or anastomotic recurrence',
            ],
          },
          {
            sectionName: 'REVIEW IMAGING FINDINGS',
            itemsList: [
              {
                path: '/Axumintraining/assets/images/acquisition/CaseStudy3.png',
                alt: 'Imaging Findings',
              },
            ],
          },
        ],
      },
      {
        title: 'Case Study 4',
        headshot: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_HEADSHOT_4,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              'Prostate cancer T3b, N0M0 Gleason 9',
              'Radiotherapy and Goserelin then Bicalutamide',
              'Initial response on MR seen with reduction of tumor size but relapse subsequently with enlarging tumor',
            ],
          },
        ],
        imaging: {
          // heading: 'DICOM VIEWER',
          // paragraph: 'Please review and evaluate the DICOM image',
          // warning: ' (Note: this may take a few minutes to load.)',
          // dicomIndex: 4,
          heading: 'PET/CT IMAGES',
          paragraph: 'Please review the case images below, then click the right arrow to review the findings.',
          imagePath: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_IMAGING_4,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: [
              'Urinary activity in ureters and bladder',
              'But also intense activity left prostate but activity at prostate base too; be suspicious, window and see it is separate, no TURP history',
              'Benign inguinal nodes with no extraprostatic suspicious foci',
              'Incidental activity left superficial neck, correlates with vessel: reflux into a collateral and holdup at valve',
              'Gallstones with milk of calcium',
            ],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: ['Salvage brachytherapy left lobe only; but tumor at base grew and invaded bladder wall'],
          },
          {
            sectionName: 'REVIEW IMAGING FINDINGS',
            itemsList: [
              {
                path: '/Axumintraining/assets/images/acquisition/CaseStudy4.png',
                alt: 'Imaging Findings',
              },
            ],
          },
        ],
      },
      {
        title: 'Case Study 5',
        headshot: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_HEADSHOT_5,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              'Radiation therapy Gleason 8 cancer',
              'Negative subsequent pelvic node dissection',
              'Local relapse with Gleason 9, LHRH',
              'Rising PSA despite ADT',
              'PSA now 62 ng/mL',
            ],
          },
        ],
        imaging: {
          // heading: 'DICOM VIEWER',
          // paragraph: 'Please review and evaluate the DICOM image',
          // warning: ' (Note: this may take a few minutes to load.)',
          // dicomIndex: 5,
          heading: 'PET/CT IMAGES',
          paragraph: 'Please review the case images below, then click the right arrow to review the findings.',
          imagePath: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_IMAGING_5,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: [
              'Some urinary excretion with dependent activity in bladder',
              'Mild-moderate heterogenous prostate right > left; call positive since significantly greater than blood pool',
              'Definitively positive precarinal and right paratracheal nodes',
            ],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: [
              'Nodes enlarged on subsequent CT; explanation for very elevated PSA',
              'Prostate was not biopsied; may be seeing partially treated disease with known earlier recurrence',
            ],
          },
          {
            sectionName: 'REVIEW IMAGING FINDINGS',
            itemsList: [
              {
                path: '/Axumintraining/assets/images/acquisition/CaseStudy5.png',
                alt: 'Imaging Findings',
              },
            ],
          },
        ],
      },
      {
        title: 'Case Study 6',
        headshot: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_HEADSHOT_6,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              'Post radical prostatectomy, pT3, Gleason 9',
              'PSA postoperative 1.1 ng/mL',
              'Axumin scan 5 months later',
            ],
          },
        ],
        imaging: {
          // heading: 'DICOM VIEWER',
          // paragraph: 'Please review and evaluate the DICOM image',
          // warning: ' (Note: this may take a few minutes to load.)',
          // dicomIndex: 6,
          heading: 'PET/CT IMAGES',
          paragraph: 'Please review the case images below, then click the right arrow to review the findings.',
          imagePath: IMAGE_PATHS.CASE_STUDY[1].ADDITIONAL_CASE_STUDY_IMAGING_6,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: [
              'L3 sclerotic hot focus',
              'Postoperative bladder tracer activity',
              'Incidental mucocele left maxillary sinus, right renal cysts',
            ],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: [
              'MR initially negative, then became positive at L3 and T12 on subsequent MRI scan',
              'Probably microscopic at T12 or spread later',
            ],
          },
          {
            sectionName: 'REVIEW IMAGING FINDINGS',
            itemsList: [
              {
                path: '/Axumintraining/assets/images/acquisition/CaseStudy6.png',
                alt: 'Imaging Findings',
              },
            ],
          },
        ],
      },
    ],
  },
  poslumaAdditional: {
    '0': [
      {
        title: 'Case Study 1',
        headshot: IMAGE_PATHS.CASE_STUDY[2].ADDITIONAL_CASE_STUDY_HEADSHOT_1,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              '81 years of age',
              'High risk patient',
              'Primary Gleason score of 9',
              'cT2c Nx Mx',
              'PSA at POSLUMA scan of 10.3 ng/mL',
              'POSLUMA scan acquired on 02/2021',
            ],
          },
        ],
        imaging: {
          heading: 'PET/CT IMAGES',
          childNode: AdditionalCaseStudyImaging01,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: ['Intraprostatic primary tumor', 'Extrapelvic lymph nodes', 'Multiple bone lesions'],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: [
              'Patient did not proceed to surgery due to multiple bone metastasis',
              'Lymph node metastasis could not be verified by histopathology',
              'Bone metastasis verified by conventional imaging',
            ],
          },
        ],
      },
      {
        title: 'Case Study 2',
        headshot: IMAGE_PATHS.CASE_STUDY[2].ADDITIONAL_CASE_STUDY_HEADSHOT_2,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              '74 years of age',
              'High risk patient',
              'Primary Gleason score of 9',
              'cT2 N0 M0',
              'PSA at POSLUMA scan of 11.06 ng/mL',
              'POSLUMA scan acquired on 11/2020',
            ],
          },
        ],
        imaging: {
          heading: 'PET/CT IMAGES',
          childNode: AdditionalCaseStudyImaging02,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: ['Intraprostatic multifocal primary tumor', 'Single pelvic lymph node metastasis'],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: ['Lymph node metastasis verified by histopathology'],
          },
        ],
      },
      {
        title: 'Case Study 3',
        headshot: IMAGE_PATHS.CASE_STUDY[2].ADDITIONAL_CASE_STUDY_HEADSHOT_3,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              '71-year-old patient, diagnosed at age 69',
              'Initial diagnosis received on 9/2018',
              'Biopsy Gleason score of 9',
              'Surgical Gleason score of 9',
              'T3b N0',
              'PSA of 0 ng/mL on 9/2019',
              'PSA of 0.40 ng/mL on 5/2020, and 0.80 ng/mL on 8/2020',
              'Patient had undergone radical prostatectomy and hormone therapy before scan',
              'POSLUMA scan acquired on 10/2020',
            ],
          },
        ],
        imaging: {
          heading: 'PET/CT IMAGES',
          childNode: AdditionalCaseStudyImaging03,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: ['Retroperitoneal lymph nodes', 'Gynecomastia with low uptake (benign)'],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: ['Retroperitoneal lymph nodes verified by Axumin<sup>®</sup> (fluciclovine F 18)'],
          },
          {
            itemsList: [
              {
                path: '/Poslumatraining/assets/images/global/1002224-Comparison-arrows-new.png',
                alt: '',
              },
            ],
          },
        ],
      },
      {
        title: 'Case Study 4',
        headshot: IMAGE_PATHS.CASE_STUDY[2].ADDITIONAL_CASE_STUDY_HEADSHOT_4,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              '76-year-old patient diagnosed with prostate cancer at age 64',
              'Initially diagnosed on 02/2009',
              'Biopsy Gleason score of 9',
              'Surgical Gleason score of 9',
              'T3b N0 M0',
              'Patient had undergone radical prostatectomy on 04/2012',
              'PSA values were undetectable in 05/2018',
              'PSA of 0.40 ng/mL on 1/2020 and 1.00 ng/mL on 9/2020',
              'POSLUMA scan acquired on 12/2020',
              'Patient had received bicalutamide and hormone therapy',
            ],
          },
        ],
        imaging: {
          heading: 'PET/CT IMAGES',
          childNode: AdditionalCaseStudyImaging04,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: ['Bone lesion in the spine (T5)', 'Bone lesion in the 7th rib, on the left side'],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: [
              'Bone lesions verified by histology',
              'Lesion in the spine verified by CT',
              'Rib lesion verified with Axumin<sup>®</sup> (fluciclovine F 18)',
            ],
          },
          {
            itemsList: [
              {
                path: '/Poslumatraining/assets/images/global/1002237-Comparison.png',
                alt: '',
              },
            ],
          },
        ],
      },
      {
        title: 'Case Study 5',
        headshot: IMAGE_PATHS.CASE_STUDY[2].ADDITIONAL_CASE_STUDY_HEADSHOT_5,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              '76-year-old patient initially diagnosed with prostate cancer at age 73',
              'Date of diagnosis was 06/2017',
              'Biopsy Gleason score of 9',
              'Surgical Gleason score of 9',
              'T3b N1',
              'Patient had undergone radical prostatectomy on 07/2017',
              'Patient had previously received docetaxel and hormone therapy ',
              'Prior to scan, PSA of 0.32 ng/mL (09/2017), 0.01 ng/mL (11/2019), 0.03ng/mL (02/2020), 0.17 ng/mL (06/2020), and 0.28 ng/mL (07/2020)',
              'PSA doubling time of 1.5 months',
              'POSLUMA scan acquired on 08/2020',
            ],
          },
        ],
        imaging: {
          heading: 'PET/CT IMAGES',
          childNode: AdditionalCaseStudyImaging05,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: ['Prostate bed', 'Internal iliac lymph node', 'Presacral lymph node'],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: [
              'Prostate bed lesion verified with Axumin<sup>®</sup> (fluciclovine F 18)',
              'Internal iliac lymph node verified with CT and Axumin (fluciclovine F 18)',
              'Presacral lymph node verified with CT',
            ],
          },
          {
            itemsList: [
              {
                path: '/Poslumatraining/assets/images/global/1007205-Comparison.png',
                alt: '',
              },
            ],
          },
        ],
      },
      {
        title: 'Case Study 6',
        headshot: IMAGE_PATHS.CASE_STUDY[2].ADDITIONAL_CASE_STUDY_HEADSHOT_6,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              '60-year-old patient diagnosed at age 58',
              'Date of initial diagnosis was 06/2019',
              'Surgical Gleason score of 7',
              'T3a N0 M0',
              'Patient had received hormone therapy',
              'Patient had undergone radical prostatectomy on 06/2019',
              'Prior to scan, PSA of 4.20 ng/mL (09/2019), 6.22 ng/mL (10/2019), 0.32ng/mL (10/2020)',
              'POSLUMA scan acquired on 01/2021',
            ],
          },
        ],
        imaging: {
          heading: 'PET/CT IMAGES',
          // imagePath: IMAGE_PATHS.CASE_STUDY[2].ADDITIONAL_CASE_STUDY_IMAGING_6,
          childNode: AdditionalCaseStudyImaging06,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: ['Pelvic lymph nodes', 'Bone lesion in the lumbar spine'],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: [
              'Lymph node lesion verified with Axumin<sup>®</sup> (fluciclovine F 18)',
              'Bone lesion verified with Axumin',
            ],
          },
          {
            itemsList: [
              {
                path: '/Poslumatraining/assets/images/global/1017228-Comparison.png',
                alt: '',
              },
            ],
          },
        ],
      },
    ],
  },
  poslumaAcquisition: {},
  poslumaInterpretation: {
    '12': [
      {
        headshot: IMAGE_PATHS.CASE_STUDY[2].CASE_STUDY_HEADSHOT_1,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: ['Gleason 9 primary prostate tumor', 'T2 Nx Mx', 'PSA of 8.3 ng/mL'],
          },
        ],
        imaging: {
          heading: 'PET/CT IMAGES',
          // paragraph: 'FPO - video to be created',
          videoPath: VIDEO_PATHS.CASE_STUDY_1.path,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: [
              'Intraprostatic lesion',
              'No scintigraphic evidence of pelvic lymph node metastases (N0) or distant metastases (M0)',
            ],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: ['Total prostatectomy', 'Pelvic lymph node resection'],
          },
        ],
      },
      {
        headshot: IMAGE_PATHS.CASE_STUDY[2].CASE_STUDY_HEADSHOT_2,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: ['Gleason 7 primary prostate tumor', 'cT2 N0 M0', 'At initial diagnosis, PSA of 11.0 ng/mL'],
          },
        ],
        imaging: {
          heading: 'PET/CT IMAGES',
          // paragraph: 'FPO - video to be created',
          videoPath: VIDEO_PATHS.CASE_STUDY_2.path,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: [
              'Primary tumor identified in right prostate lobe',
              'Internal iliac lymph node uptake',
              'Bone metastases in the right ilium and the sacrum',
            ],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: [
              'Due to multiple bone lesions, patient did not undergo surgery',
              'Lymph node metastases could not be verified',
              'With POSLUMA, patient may be staged as N1M1b',
            ],
          },
        ],
      },
    ],

    '13': [
      {
        headshot: IMAGE_PATHS.CASE_STUDY[2].CASE_STUDY_HEADSHOT_3,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              'Robotic-assisted prostatectomy and pelvic lymph node dissection in May 2020 following Gleason 9 prostate tumor',
              'T3N1',
              'PSA of 0.26 ng/mL 4 months after surgery',
            ],
          },
        ],
        imaging: {
          heading: 'PET/CT IMAGES',
          // paragraph: 'FPO - video to be created',
          videoPath: VIDEO_PATHS.CASE_STUDY_3.path,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: [
              'Two lymph node metastases (one presacral node and one left common iliac node)',
              'No other pathological findings',
            ],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: ['Common iliac node metastasis verified with histopathology'],
          },
        ],
      },
      {
        headshot: IMAGE_PATHS.CASE_STUDY[2].CASE_STUDY_HEADSHOT_4,
        presentation: [
          {
            sectionName: 'PATIENT PRESENTATION',
            itemsList: [
              'Gleason 7 T2b primary prostate tumor',
              'Previous prostatectomy followed by salvage radiotherapy',
              'At time of scan, PSA of 12.53 ng/mL',
            ],
          },
        ],
        imaging: {
          heading: 'PET/CT IMAGES',
          // paragraph: 'FPO - video to be created',
          videoPath: VIDEO_PATHS.CASE_STUDY_4.path,
        },
        findings: [
          {
            sectionName: 'IMAGING FINDINGS',
            itemsList: ['Local recurrence in right seminal vesicle', 'Bone metastasis in C2 vertebra'],
          },
          {
            sectionName: 'FOLLOW-UP',
            itemsList: [
              'Local recurrence not verified by histology',
              'Bone lesion observed on MRI scan',
              'Local recurrence and bone lesion observed on Axumin PET scan',
            ],
          },
        ],
      },
    ],
  },
};

export default CASE_STUDY;
